/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  HeatMix,
  MaterialQuantityUnit,
  MaterialRead,
  MixBasketLayer,
} from "src/store/api/generatedApi";
import { sorted } from "src/helpers";
import {
  BasketComparisonResult,
  MixViewAs,
  ProcessedBasketComparison,
  ProcessedBasketSource,
  ProcessedMaterial,
  ProcessedNonBasketSource,
} from "../types";

const layerUnitForView = (mixView: MixViewAs): MaterialQuantityUnit => {
  switch (mixView) {
    case "mass":
      return "mass";
    case "lifts":
      return "grab";
    case "liftRecipe":
      return "ratio";
  }
};

interface GlobalSlot {
  name: string;
  materialOrdinal: number;
}
interface SourceLayerEntry {
  name: string;
  value: number;
}
interface SourceInfo {
  layers: SourceLayerEntry[];
  hasBasket: boolean;
  globalToLayer: Map<number, number>;
}

/**
 * Longest common subsequence suffix table followed by forward greedy reconstruction.
 *
 * Returns pairs [sourceIndex, globalIndex] that form the longest common
 * subsequence (by material name) between the source layer list and the
 * current global slot order.  On ties the reconstruction prefers to skip
 * the source item (treating it as a new insertion) so that existing global
 * slots are matched as early as possible.
 */
const lcsAlign = (
  sourceNames: string[],
  globalNames: string[]
): [number, number][] => {
  const sourceLength = sourceNames.length;
  const globalLength = globalNames.length;

  // f[i][j] = LCS length of sourceNames[i:] vs globalNames[j:]
  const suffixLcs: number[][] = Array.from({ length: sourceLength + 1 }, () =>
    Array<number>(globalLength + 1).fill(0)
  );
  for (let i = sourceLength - 1; i >= 0; i--) {
    for (let j = globalLength - 1; j >= 0; j--) {
      suffixLcs[i]![j] =
        sourceNames[i] === globalNames[j]
          ? suffixLcs[i + 1]![j + 1]! + 1
          : Math.max(suffixLcs[i + 1]![j]!, suffixLcs[i]![j + 1]!);
    }
  }

  const matches: [number, number][] = [];
  let sourcePointer = 0;
  let globalPointer = 0;
  while (sourcePointer < sourceLength && globalPointer < globalLength) {
    const current = suffixLcs[sourcePointer]![globalPointer]!;
    if (
      sourceNames[sourcePointer] === globalNames[globalPointer] &&
      suffixLcs[sourcePointer + 1]![globalPointer + 1] === current - 1
    ) {
      matches.push([sourcePointer, globalPointer]);
      sourcePointer++;
      globalPointer++;
    } else if (
      suffixLcs[sourcePointer + 1]![globalPointer]! >=
      suffixLcs[sourcePointer]![globalPointer + 1]!
    ) {
      sourcePointer++;
    } else {
      globalPointer++;
    }
  }
  return matches;
};

/**
 * Filter and sort a basket's layers for the active view, returning only
 * visible basket-addition materials in layer_number order.
 */
const visibleLayers = (
  layers: MixBasketLayer[],
  targetUnit: MaterialQuantityUnit,
  ignoredMaterials: Set<string>,
  materialAdditionLocation: Map<string, string | undefined>
): SourceLayerEntry[] =>
  sorted(layers, (layer) => layer.layer_number)
    .filter(
      (layer) =>
        layer.quantity.unit === targetUnit &&
        !ignoredMaterials.has(layer.material_name) &&
        materialAdditionLocation.get(layer.material_name) === "basket"
    )
    .map((layer) => ({
      name: layer.material_name,
      value: layer.quantity.value,
    }));

/**
 * Merge a new source's layers into the current global slot order using
 * LCS alignment.  Returns the updated global order and the mapping from
 * new global index → source layer index for the new source.  Also
 * remaps all prior source mappings to account for inserted slots.
 */
const mergeSourceIntoGlobalOrder = (
  layers: SourceLayerEntry[],
  globalOrder: readonly GlobalSlot[],
  priorSources: SourceInfo[],
  nextOrdinal: (name: string) => number
): { mergedGlobal: GlobalSlot[]; newMapping: Map<number, number> } => {
  const sourceNames = layers.map((layer) => layer.name);
  const globalNames = globalOrder.map((slot) => slot.name);
  const matches = lcsAlign(sourceNames, globalNames);

  const mergedGlobal: GlobalSlot[] = [];
  const oldToNewIndex = new Map<number, number>();
  const newMapping = new Map<number, number>();

  let globalCopyPointer = 0;
  let matchIndex = 0;

  for (let sourceIndex = 0; sourceIndex < layers.length; sourceIndex++) {
    const match = matches[matchIndex];
    if (match?.[0] === sourceIndex) {
      const matchedGlobalIndex = match[1];
      while (globalCopyPointer <= matchedGlobalIndex) {
        oldToNewIndex.set(globalCopyPointer, mergedGlobal.length);
        mergedGlobal.push(globalOrder[globalCopyPointer]!);
        globalCopyPointer++;
      }
      newMapping.set(oldToNewIndex.get(matchedGlobalIndex)!, sourceIndex);
      matchIndex++;
    } else {
      newMapping.set(mergedGlobal.length, sourceIndex);
      mergedGlobal.push({
        name: layers[sourceIndex]!.name,
        materialOrdinal: nextOrdinal(layers[sourceIndex]!.name),
      });
    }
  }

  while (globalCopyPointer < globalOrder.length) {
    oldToNewIndex.set(globalCopyPointer, mergedGlobal.length);
    mergedGlobal.push(globalOrder[globalCopyPointer]!);
    globalCopyPointer++;
  }

  // Remap prior source mappings to reflect shifted indices.
  for (const prior of priorSources) {
    const remapped = new Map<number, number>();
    for (const [oldIndex, layerIndex] of prior.globalToLayer) {
      const newIndex = oldToNewIndex.get(oldIndex);
      if (newIndex !== undefined) {
        remapped.set(newIndex, layerIndex);
      }
    }
    prior.globalToLayer = remapped;
  }

  return { mergedGlobal, newMapping };
};

/**
 * Build the processed material array for one source, given the shared
 * global slot order and this source's (and previous source's) layer mapping.
 */
const buildProcessedMaterials = (
  globalOrder: readonly GlobalSlot[],
  source: SourceInfo,
  previousSource: SourceInfo | undefined,
  isFirstSource: boolean,
  materialColorMap: Map<string, string>
): ProcessedMaterial[] =>
  globalOrder.map((slot, globalIndex) => {
    const layerIndex = source.globalToLayer.get(globalIndex);
    const value =
      layerIndex !== undefined ? source.layers[layerIndex]!.value : 0;
    const isPresent = layerIndex !== undefined;

    const previousLayerIndex = previousSource?.globalToLayer.get(globalIndex);
    const previousValue =
      previousLayerIndex !== undefined && previousSource
        ? previousSource.layers[previousLayerIndex]!.value
        : 0;

    return {
      name: slot.name,
      materialOrdinal: slot.materialOrdinal,
      value,
      diff: isFirstSource ? null : value - previousValue,
      isPresent,
      color: materialColorMap.get(slot.name) ?? "#888888",
    };
  });

/**
 * Transform HeatMix[] data into a structure optimized for per-basket comparison.
 *
 * This function:
 * 1. Collects all unique basket numbers across all sources
 * 2. For each basket, aligns layers across sources via LCS
 * 3. Computes diffs against the previous source (by ordering)
 * 4. Identifies non-basket materials (in totals/lifts but not in any basket)
 * 5. Handles missing baskets/materials gracefully
 */
export const processBasketComparison = (
  mixes: HeatMix[],
  ignoredMaterials: Set<string>,
  materials: MaterialRead[],
  mixView: MixViewAs = "mass"
): BasketComparisonResult => {
  const sortedMixes = sorted(mixes, (mix) => mix.ordering);

  const materialColorMap = new Map<string, string>(
    materials.map((m) => [m.name, m.colour])
  );
  const materialAdditionLocation = new Map(
    materials.map((m) => [m.name, m.addition_location])
  );

  const allBasketNumbers = new Set<number>(
    sortedMixes.flatMap((mix) =>
      mix.mix.baskets.map((basket) => basket.basket_number)
    )
  );
  const sortedBasketNumbers = sorted(Array.from(allBasketNumbers), (n) => n);

  const targetUnit = layerUnitForView(mixView);

  const baskets: ProcessedBasketComparison[] = sortedBasketNumbers.reduce<
    ProcessedBasketComparison[]
  >((result, basketNumber) => {
    // Collect filtered, sorted layers per source for this basket.
    const sourceInfos: SourceInfo[] = sortedMixes.map((mix) => {
      const basket = mix.mix.baskets.find(
        (b) => b.basket_number === basketNumber
      );
      if (!basket) {
        return {
          layers: [],
          hasBasket: false,
          globalToLayer: new Map<number, number>(),
        };
      }
      return {
        layers: visibleLayers(
          basket.layers,
          targetUnit,
          ignoredMaterials,
          materialAdditionLocation
        ),
        hasBasket: true,
        globalToLayer: new Map<number, number>(),
      };
    });

    // Build global slot ordering via incremental LCS alignment.
    // The first source with layers sets the base order; each subsequent
    // source's layers are aligned against the current global order and
    // any unmatched layers are inserted at the correct position.
    const ordinalCounters = new Map<string, number>();
    const nextOrdinal = (name: string): number => {
      const ordinal = ordinalCounters.get(name) ?? 0;
      ordinalCounters.set(name, ordinal + 1);
      return ordinal;
    };

    const globalOrder = sourceInfos.reduce<GlobalSlot[]>(
      (currentGlobal, sourceInfo, sourceIndex) => {
        if (currentGlobal.length === 0) {
          // First source with layers (or empty) — sets the base.
          sourceInfo.globalToLayer = new Map(
            sourceInfo.layers.map((_, layerIndex) => [layerIndex, layerIndex])
          );
          return sourceInfo.layers.map((layer) => ({
            name: layer.name,
            materialOrdinal: nextOrdinal(layer.name),
          }));
        }

        const priorSources = sourceInfos.slice(0, sourceIndex);
        const { mergedGlobal, newMapping } = mergeSourceIntoGlobalOrder(
          sourceInfo.layers,
          currentGlobal,
          priorSources,
          nextOrdinal
        );
        sourceInfo.globalToLayer = newMapping;
        return mergedGlobal;
      },
      []
    );

    if (globalOrder.length === 0) {
      return result;
    }

    const sources: ProcessedBasketSource[] = sortedMixes.map((mix, index) => {
      const sourceInfo = sourceInfos[index]!;
      const previousSource = index > 0 ? sourceInfos[index - 1] : undefined;

      return {
        sourceName: mix.source,
        ordering: mix.ordering,
        materials: buildProcessedMaterials(
          globalOrder,
          sourceInfo,
          previousSource,
          index === 0,
          materialColorMap
        ),
        hasBasket: sourceInfo.hasBasket,
      };
    });

    return [
      ...result,
      { basketNumber, sources, allMaterials: [...globalOrder] },
    ];
  }, []);

  // Identify non-basket materials (addition_location !== "basket") from the
  // appropriate data source based on mixView.
  const getNonBasketValue = (
    mix: HeatMix,
    materialName: string
  ): number | undefined => {
    switch (mixView) {
      case "mass":
        return mix.mix.totals.find((t) => t.material_name === materialName)
          ?.mass;
      case "lifts":
        return mix.mix.lifts.find((t) => t.material_name === materialName)
          ?.lifts;
      case "liftRecipe": {
        const lift = mix.mix.lifts.find(
          (t) => t.material_name === materialName
        );
        return lift ? (lift.normalised_lifts ?? lift.lifts) : undefined;
      }
    }
  };

  const allNonBasketMaterialNames = new Set<string>(
    sortedMixes.flatMap((mix) => {
      const entries = mixView === "mass" ? mix.mix.totals : mix.mix.lifts;
      return entries
        .map((entry) => entry.material_name)
        .filter((name) => !ignoredMaterials.has(name));
    })
  );

  const sortedNonBasketMaterials = sorted(
    Array.from(allNonBasketMaterialNames).filter(
      (name) => materialAdditionLocation.get(name) !== "basket"
    ),
    (name) => materials.find((m) => m.name === name)?.ordering ?? 999
  );

  const nonBasketMaterials: ProcessedNonBasketSource[] = sortedMixes.map(
    (mix, index) => {
      const previousMix = index > 0 ? sortedMixes[index - 1] : undefined;

      const processedMaterials: ProcessedMaterial[] =
        sortedNonBasketMaterials.map((materialName) => {
          const value = getNonBasketValue(mix, materialName) ?? 0;
          const previousValue = previousMix
            ? (getNonBasketValue(previousMix, materialName) ?? 0)
            : 0;
          const isPresent = getNonBasketValue(mix, materialName) !== undefined;

          return {
            name: materialName,
            materialOrdinal: 0,
            value,
            diff: index === 0 ? null : value - previousValue,
            isPresent,
            color: materialColorMap.get(materialName) ?? "#888888",
          };
        });

      return {
        sourceName: mix.source,
        ordering: mix.ordering,
        materials: processedMaterials,
      };
    }
  );

  return {
    baskets,
    nonBasketMaterials,
    hasNonBasketMaterials: sortedNonBasketMaterials.length > 0,
  };
};
